/**
 * use-way-out.ts — a layer that leaves in its own time: drawn on after it is
 * taken away, while its sheet plays its way out on the element that leaves,
 * still showing what it showed last, and let go as that animation ends — at
 * once under reduced motion, so no way out is left to play later. Modal and
 * the lobby leave this way; the element that leaves is inert meanwhile, so
 * nothing on it takes a press, a key or a screen reader, and no gold on it is
 * counted.
 *
 *   const way = useWayOut(open, children);
 *   if (!open && !way.leaving) return null;
 *   <div inert={way.leaving} data-leaving={way.leaving ? '' : undefined}
 *        onAnimationEnd={way.leaving ? way.left : undefined}>{way.shown}</div>
 *
 * `shown` is `value` while the layer is present, and the last value it had
 * then while it leaves: a caller may take its words away as it closes.
 * `appearances` counts the times it has come, so a layer that comes back
 * while it is still leaving can be drawn afresh (keyed by it) rather than
 * revived.
 */

import { useState } from 'react';
import type { AnimationEvent } from 'react';

import { useReducedMotion } from './use-reduced-motion';

interface WayOut<T> {
  /** What to show: the value while present, the last one it had then while leaving. */
  shown: T;
  /** It has been taken away and is playing its way out. */
  leaving: boolean;
  /** Its own way out has ended — an animation on the element itself, not one inside it. */
  left: (event: AnimationEvent<Element>) => void;
  /** How many times it has come. */
  appearances: number;
}

export function useWayOut<T>(present: boolean, value: T): WayOut<T> {
  const reduced = useReducedMotion();
  const [held, setHeld] = useState(value);
  const [wasPresent, setWasPresent] = useState(present);
  const [leaving, setLeaving] = useState(false);
  const [appearances, setAppearances] = useState(present ? 1 : 0);
  if (present && held !== value) setHeld(value);
  if (present !== wasPresent) {
    setWasPresent(present);
    setLeaving(!present && !reduced);
    if (present) setAppearances(appearances + 1);
  }
  const left = (event: AnimationEvent<Element>) => {
    if (event.target === event.currentTarget) setLeaving(false);
  };
  return { shown: present ? value : held, leaving, left, appearances };
}
